/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'./SmartsRecommenderService',
	'./SmartsRecommenderError'
], function (BaseService, SmartsError) {
	'use strict';

	const SMARTS_ROOT = 'v1/smarts';
	const CHART_INFO = SMARTS_ROOT + '/visualization-recommender/chartInfo';
	const ALTERNATE_VISUALIZATION_URL = SMARTS_ROOT + '/visualization-recommender/recommend';
	const RELATED_VISUALIZATION_URL = SMARTS_ROOT + '/insights/related-visualizations';
	// This Smarts API will return first vis as the best vis for target fields, and other vis for related fields
	// of the target fields
	const RECOMMENDER_RELATED_VISUALIZATION_URL = SMARTS_ROOT + '/explore/related-visualizations';

	const RECOMMENDER_COMPARE_VISUALIZATION_URL = SMARTS_ROOT + '/content-recommender/comparison';

	var SmartsVisRecommenderService = BaseService.extend({

		init: function(options) {
			SmartsVisRecommenderService.inherited('init', this, arguments);
			this.ajaxSvc = options.ajaxSvc;
			this.logger = options.logger;
		},

		/**
		 * Get result from chartInfo endpoint
		 */
		getChartInfo: function(requestParams) {
			this.logger.debug('getRecommendInfo');

			return this._executeRequest(requestParams, CHART_INFO)
				.catch(this._handleError.bind(this))
				.then(({ data }) => {
					if (data.smartsStatus !== SmartsError.STATUS.OK) {
						throw new SmartsError(data.smartsStatus, data.details);
					}

					// Recommender may return more than one answer that matches the same slot mappings.
					// In some cases it may return those mappings plus unmapped slots with the 'mandatorySlotMissing' Flag set.
					// Give preference to the first complete one as the title source (unless no complete ones are returned).
					let chartInfo = data;
					if (data.recommendations) {
						const completeRecommendations = data.recommendations.filter((recommendation) => !recommendation.mandatorySlotsMissing);
						chartInfo = (completeRecommendations && completeRecommendations.length) ? completeRecommendations[0] : data.recommendations[0];
					}
					return {
						title: chartInfo.title,
						label: chartInfo.label,
						description: chartInfo.description,
						mandatorySlotsMissing: chartInfo.mandatorySlotsMissing
					};
				});
		},

		recommendAlternateVisualizationsOrBindings: function(requestParams) {
			this.logger.debug('recommendAlternateVisualizationsOrBindings');

			return this._executeRequest(requestParams, ALTERNATE_VISUALIZATION_URL)
				.catch(this._handleError.bind(this))
				.then(this._processResponse.bind(this));
		},

		recommendRelatedVisualizations: function(requestParams) {
			let url = RELATED_VISUALIZATION_URL;
			if (requestParams.includeBestVisForTargetFields) {
				url = RECOMMENDER_RELATED_VISUALIZATION_URL;
			}
			this.logger.debug('recommendRelatedVisualizations');

			return this._executeRequest(requestParams, url)
				.catch(this._handleError.bind(this))
				.then(this._processResponse.bind(this));
		},

		recommendCompareVisualizations: function(requestParams) {
			let url = RECOMMENDER_COMPARE_VISUALIZATION_URL;

			this.logger.debug('recommendCompareVisualizations');

			return this._executeRequest(requestParams, url)
				.catch(this._handleError.bind(this))
				.then(this._processCompareResponse.bind(this));
		},

		_processResponse: function(ajaxResponse) {
			const recommenderResponse = ajaxResponse.data;
			if (recommenderResponse.smartsStatus === SmartsError.STATUS.OK) {
				return recommenderResponse.recommendations;
			} else {
				throw new SmartsError(recommenderResponse.smartsStatus,recommenderResponse.details);
			}
		},

		_processCompareResponse: function(ajaxResponse) {
			const recommenderResponse = ajaxResponse.data;
			if (recommenderResponse.status.code === SmartsError.STATUS.OK) {
				return recommenderResponse.ircontent;
			} else {
				throw new SmartsError(recommenderResponse.smartsStatus,recommenderResponse.details);
			}
		},

		_handleError: function(ajaxError){
			const responseJSON = ajaxError.jqXHR.responseJSON;
			this.logger.debug(responseJSON);
			if (responseJSON) {
				throw new SmartsError( responseJSON.smartsStatus, responseJSON.details );
			} else {
				throw new SmartsError( ajaxError.jqXHR.fail().status, ajaxError.jqXHR.fail().responseText );
			}
		}

	});

	return SmartsVisRecommenderService;
});
